import Link from 'next/link';
import React, { CSSProperties, FC } from 'react';
import { TbChevronRight } from 'react-icons/tb';

import { useDraggable } from '@dnd-kit/core';
import { Alert, Anchor, Box, Card, Flex, Group, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import { useHover, useMousePosition } from '@mantine/hooks';

import { ComponentIcon } from '@diagram/components/diagram-component-instance/ComponentIcon';
import { DraggableCategoryGridItem } from '@diagram/components/diagram-sidebar/sidebars/add-component-instance/CategoryGridItem';
import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';

import { Component, SavedItemType } from 'models';
import { ComponentHelpers } from 'helpers/ComponentHelpers';
import { deNullRef } from 'helpers/de-null-ref';
import { isTouchDevice } from 'helpers/isTouchDevice';

import { AIEnabledBadge } from 'components/badges/AIEnabledBadge';
import { SaveButton } from 'components/buttons/SaveButton';
import { CompanyLogo } from 'components/company-logo/CompanyLogo';
import { ComponentDiagramButton } from 'components/component-design-button/ComponentDiagramButton';
import { SettingsDropdown } from 'components/dropdown/SettingsDropdown';
import { ComponentThumbnail } from 'components/thumbnail/ComponentThumbnail';
import { useAction } from 'hooks/use-action';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useComponentMeta } from 'hooks/use-component-meta';
import { useCurrentUser } from 'hooks/use-current-user';
import { useDistributorPrice } from 'hooks/use-distributor-price';
import { ComponentService } from 'services/ComponentService';
import { DraggingType } from 'types/DraggingType';

import type { ComponentPricing } from '@/payload-types';

import { DiagramComponentOverviewHitDatasheet } from './ComponentOverviewHitDatasheet.Diagram';
import { ComponentOverviewHitDistributorPrice } from './ComponentOverviewHitDistributorPrice';

import cx from './ComponentOverviewHit.Diagram.module.scss';

const IMAGE_WIDTH = 40;

type Props = {
    component: Component;
    distributorPrice?: ComponentPricing | null;
    showSpecifications?: boolean;
    setShowSpecifications?: () => void;
    showLinkButton?: boolean;
    showDisableDiagramUseMessage?: boolean;
    children?: React.ReactNode;
};

const DiagramComponentOverviewHit: FC<Props> = ({
    component,
    distributorPrice,
    showLinkButton = true,
    showSpecifications = true,
    setShowSpecifications,
    showDisableDiagramUseMessage,
    children,
}) => {
    const user = useCurrentUser();

    const { company: manufacturer } = useCompanyProfile(component.manufacturer);

    const { hovered, ref } = useHover();

    const { disableDiagramUse } = ComponentInstanceHelpers.definition(component.type);

    const internalShowLinkButton = showLinkButton && user;

    const showAi = !!component?.files?.length;
    const isTeamComponent = ComponentService.isTeamComponent(component);

    const [handleDelete, deleting, deleted] = useAction(async () => {
        await ComponentService.delete(component.id);
    });

    if (deleted) return null;

    return (
        <Card ref={deNullRef(ref)} withBorder h="100%" shadow={hovered ? 'xs' : undefined} p="sm" className={cx.root}>
            <Flex align="flex-start" gap="xs">
                {/* left side */}
                <Stack gap="sm" style={{ flexGrow: 1 }}>
                    <DiagramComponentOverviewHitTitle component={component} distributorPrice={distributorPrice} />

                    {showSpecifications ? (
                        <DiagramComponentOverviewHitDatasheet component={component} />
                    ) : setShowSpecifications ? (
                        <Group>
                            <Anchor
                                component="button"
                                onClick={() => {
                                    setShowSpecifications();
                                }}
                            >
                                <Group gap={2}>
                                    <span>Expand specifications</span>
                                    <TbChevronRight size={14} />
                                </Group>
                            </Anchor>
                        </Group>
                    ) : null}

                    {(internalShowLinkButton || showAi) && (
                        <Group justify="between" gap="md">
                            {internalShowLinkButton && <ComponentDiagramButton component={component} />}
                            {showAi && (
                                <Group gap={4}>
                                    {showAi && (
                                        <UnstyledButton
                                            component={Link}
                                            href={`${ComponentHelpers.urls.view(component.id)}?action=chat`}
                                            target="_blank"
                                        >
                                            <AIEnabledBadge />
                                        </UnstyledButton>
                                    )}
                                </Group>
                            )}
                        </Group>
                    )}
                </Stack>

                {/* right side */}
                <Stack gap="md">
                    {manufacturer && <CompanyLogo logos={manufacturer.logos} width={IMAGE_WIDTH} />}

                    <ComponentThumbnail
                        showEmpty
                        component={component}
                        style={{
                            flexShrink: 0,
                            flexGrow: 0,
                            width: IMAGE_WIDTH,
                            height: IMAGE_WIDTH,
                        }}
                    />
                </Stack>
            </Flex>

            <Box className={cx.actions}>
                {isTeamComponent ? (
                    <SettingsDropdown loading={deleting}>
                        <SettingsDropdown.Edit
                            component={Link}
                            href={ComponentHelpers.urls.view(component.id)}
                            target="_blank"
                        >
                            Edit
                        </SettingsDropdown.Edit>
                        <SettingsDropdown.Delete onClick={handleDelete}>Delete</SettingsDropdown.Delete>
                    </SettingsDropdown>
                ) : (
                    <SaveButton id={component.id} name={component.name} type={SavedItemType.COMPONENT} iconOnly />
                )}
            </Box>

            {children}

            {showDisableDiagramUseMessage && disableDiagramUse && (
                <Alert color="orange" fz="xs" p="xs" mt="xs">
                    Component cannot be used in diagrams
                </Alert>
            )}
        </Card>
    );
};

const DiagramComponentOverviewHitTitle: FC<{
    component: Component;
    distributorPrice?: ComponentPricing | null;
}> = ({ component, distributorPrice: providedPrice }) => {
    const { uniqueName, subtitle } = useComponentMeta(component);
    const { distributorPrice: ownPrice } = useDistributorPrice(providedPrice === undefined ? component.id : null);
    const distributorPrice = providedPrice ?? ownPrice;

    return (
        <Stack gap={4}>
            <Box>
                <Anchor component={Link} href={ComponentHelpers.urls.view(component.id)} target="_blank">
                    <Title order={3} fz="md" fw={700} style={{ display: 'inline' }}>
                        {uniqueName}
                    </Title>
                </Anchor>
                <Text c="gray.5" fz="sm" fw={600}>
                    {subtitle}
                </Text>
            </Box>
            {distributorPrice && <ComponentOverviewHitDistributorPrice distributorPrice={distributorPrice} />}
        </Stack>
    );
};

const DesktopDraggableDiagramComponentOverviewHit: FC<Props> = ({ component, ...props }) => {
    const { x, y } = useMousePosition();

    const { disableDiagramUse } = ComponentInstanceHelpers.definition(component.type);

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `draggable-new-${component.type}-${component.id}`,
        data: {
            type: DraggingType.NEW_COMPONENT_INSTANCE,
            componentType: component.type,
            componentId: component.id,
            specifications: { electrical: component.electrical },
        },
        disabled: disableDiagramUse,
    });

    const draggingStyles: CSSProperties = isDragging
        ? {
              position: 'fixed',
              left: x - 30,
              top: y - 30,

              zIndex: 9999,
          }
        : {};

    return (
        <Box>
            {isDragging && (
                <DiagramComponentOverviewHit component={component} {...props} showDisableDiagramUseMessage />
            )}

            <Box
                ref={setNodeRef}
                {...attributes}
                {...listeners}
                style={{
                    cursor: 'move',
                    userSelect: 'none',

                    ...draggingStyles,
                }}
            >
                {isDragging ? (
                    <ComponentIcon component={component} />
                ) : (
                    <DiagramComponentOverviewHit component={component} {...props} showDisableDiagramUseMessage />
                )}
            </Box>
        </Box>
    );
};

const TouchDraggableDiagramComponentOverviewHit: FC<Props> = ({ component, ...props }) => {
    const { disableDiagramUse } = ComponentInstanceHelpers.definition(component.type);

    if (disableDiagramUse) {
        return <DiagramComponentOverviewHit component={component} {...props} showDisableDiagramUseMessage />;
    }

    return (
        <DiagramComponentOverviewHit component={component} {...props} showDisableDiagramUseMessage>
            <Group gap={8} mt="xs">
                <Box w={60} h={60}>
                    <DraggableCategoryGridItem
                        withDragHandle={false}
                        type={component.type}
                        dragOptions={{
                            id: `draggable-new-${component.type}-${component.id}`,
                            data: {
                                type: DraggingType.NEW_COMPONENT_INSTANCE,
                                componentType: component.type,
                                componentId: component.id,
                                specifications: { electrical: component.electrical },
                            },
                        }}
                    />
                </Box>
                <Text c="dimmed" fz="xs">
                    Drag onto diagram
                </Text>
            </Group>
        </DiagramComponentOverviewHit>
    );
};

const DraggableDiagramComponentOverviewHit: FC<Props> = (props) => {
    const isTouch = isTouchDevice();

    return isTouch ? (
        <TouchDraggableDiagramComponentOverviewHit {...props} />
    ) : (
        <DesktopDraggableDiagramComponentOverviewHit {...props} />
    );
};

export { DiagramComponentOverviewHit, DraggableDiagramComponentOverviewHit };
